 
import { createFileRoute } from "@tanstack/react-router";
import { Users, Search, Table, Grid3X3 } from "lucide-react";
import type { TenantRead } from "@harmonyso/tenants-sdk";
import { useState, useMemo } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CreateTenantDialog } from "@/components/tenants/create-tenant-dialog";
import { TenantsSearchBar } from "@/components/tenants/tenants-search-bar";
import { TenantsGrid } from "@/components/tenants/tenants-grid";
import { TenantsTable } from "@/components/tenants/tenants-table";
import { PaginationControls } from "@/components/tenants/pagination-controls";
import { CellFailuresAlert } from "@/components/cells/cell-failures-alert";
import { useAllCellTenants } from "@/hooks/use-cell-tenants";

export const Route = createFileRoute("/tenants/")({
  component: TenantsPage,
});

type ViewMode = 'grid' | 'table';
type SortField = 'company' | 'created_at';
type SortOrder = 'asc' | 'desc';

function TenantsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [sortField, setSortField] = useState<SortField>('company');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const {
    tenants: allTenants,
    failures,
    loadingCells,
    isLoading,
    allFailed,
  } = useAllCellTenants();
  const totalTenantsCount = allTenants.length;

  // Reset to first page when search query changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Filter, sort and paginate tenants
  const filteredAndPaginatedTenants = useMemo(() => {
    // Filter tenants based on search query
    let filteredTenants = allTenants.filter((tenant: TenantRead) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        tenant.company.toLowerCase().includes(searchLower) ||
        tenant.subdomain.toLowerCase().includes(searchLower) ||
        tenant.id.toLowerCase().includes(searchLower) ||
        tenant.industry?.toLowerCase().includes(searchLower) ||
        tenant.size?.toLowerCase().includes(searchLower) ||
        tenant.subscription?.toLowerCase().includes(searchLower)
      );
    });

    // Sort tenants
    filteredTenants = filteredTenants.sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;

      if (sortField === 'company') {
        aValue = a.company.toLowerCase();
        bValue = b.company.toLowerCase();
      } else {
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
      }

      if (aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });

    // Calculate pagination
    const totalCount = filteredTenants.length;
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedTenants = filteredTenants.slice(startIndex, endIndex);

    return {
      items: paginatedTenants,
      totalPages,
      totalCount,
    };
  }, [allTenants, searchQuery, currentPage, itemsPerPage, sortField, sortOrder]);

  if (isLoading) {
    return (
      <div className="flex h-full min-w-0 flex-1 items-center justify-center">
        <div className="border-primary h-12 w-12 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  if (allFailed) {
    return (
      <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-hidden">
        <div className="shrink-0 flex flex-row items-center justify-between">
          <div>
            <h2 className="text-3xl font-semibold tracking-[-1%]">Tenants</h2>
            <p className="text-muted-foreground">
              View and manage your tenant configurations
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto">
          <CellFailuresAlert failures={failures} />
          <Card>
            <CardContent className="pt-6">
              <div className="py-12 text-center">
                <Users className="mx-auto mb-4 h-12 w-12 text-red-500" />
                <h3 className="mb-2 text-lg font-semibold">
                  Failed to Load Tenants
                </h3>
                <p className="text-muted-foreground mx-auto max-w-md">
                  None of the cells could be loaded. See the details above.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-hidden">
        <div className="shrink-0">
          <h2 className="text-3xl font-semibold tracking-[-1%]">Tenants</h2>
          <p className="text-muted-foreground">
            View and manage your tenant configurations
          </p>
        </div>

        <CellFailuresAlert failures={failures} />
        {loadingCells.length > 0 && (
          <p className="text-muted-foreground shrink-0 text-sm" role="status">
            Loading tenants from {loadingCells.map((cell) => cell.id).join(", ")}…
          </p>
        )}

        {totalTenantsCount > 0 ? (
          <div className="flex flex-1 flex-col gap-4 overflow-hidden">
            <div className="flex shrink-0 items-center justify-between gap-4">
              <div className="flex-1">
                <TenantsSearchBar
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  totalCount={totalTenantsCount}
                  filteredCount={filteredAndPaginatedTenants.totalCount}
                />
              </div>
              <div className="flex items-center border rounded-md">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    setViewMode('table');
                  }}
                  className="rounded-r-none border-r"
                >
                  <Table className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    setViewMode('grid');
                  }}
                  className="rounded-l-none"
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto">
              {filteredAndPaginatedTenants.totalCount === 0 && searchQuery ? (
                <div className="py-8 text-center">
                  <Search className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
                  <h3 className="mb-2 text-lg font-semibold">No Tenants Found</h3>
                  <p className="text-muted-foreground">
                    No tenants match your search for "{searchQuery}".
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleSearchChange("");
                    }}
                    className="mt-4"
                  >
                    Clear Search
                  </Button>
                </div>
              ) : (
                <>
                  {viewMode === 'grid' ? (
                    <TenantsGrid tenants={filteredAndPaginatedTenants.items} />
                  ) : (
                    <TenantsTable
                      tenants={filteredAndPaginatedTenants.items}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                  )}
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={filteredAndPaginatedTenants.totalPages}
                    onPageChange={setCurrentPage}
                  />
                </>
              )}
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="py-12 text-center">
                <Users className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No Tenants Found</h3>
                <p className="text-muted-foreground mx-auto mb-4 max-w-md">
                  Get started by creating your first tenant configuration.
                </p>
                <CreateTenantDialog />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
}
